import { useQuery } from '@tanstack/react-query';
import { getClassroomData } from 'api/classroomApi';
import { useUser } from 'context/UserContext';
import PlayerWidget from 'pages/components/PlayerWidget';
import React, { useEffect, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import TeamHeader from './components/TeamHeader';
import Loading from 'pages/components/Loading';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { getOtherUser } from 'api/userApi';
import { toast } from 'react-toastify';
import { getRankingData } from 'api/rankingApi';
import { getPosition } from 'pages/ranking/Ranking';
import { titler } from 'helpers/titler';

const Team = ({ other = false }) => {
  const loggedInUser = useUser();
  const [user, setUser] = useState(loggedInUser);
  const { username } = useParams();
  const navigate = useNavigate();

  if (username === loggedInUser.username) {
    return <Navigate to="/team" />;
  }

  if (other) {
    titler(`Squadra di ${username}`);
  } else {
    titler('Squadra');
  }

  const {
    data: ranking,
    isLoading: isLoadingRanking,
    isError: isErrorRanking,
    error: rankingError
  } = useQuery({
    queryKey: ['rankingData'],
    queryFn: getRankingData
  });

  const {
    data: otherUserData,
    isLoading: isLoadingOtherUser,
    error: otherUserError,
    isSuccess: otherUserSuccess
  } = useQuery({
    queryKey: [username],
    queryFn: () => getOtherUser(username),
    enabled: other // Only run this query if 'other' is true
  });

  const [studentsData, setStudentsData] = useState({});
  const [teachersData, setTeachersData] = useState({});

  const {
    isLoading: isLoadingClassroom,
    isSuccess,
    data: classroom,
    error: classroomError
  } = useQuery({
    queryKey: ['classroomData'],
    queryFn: getClassroomData
  });

  useEffect(() => {
    if (other && otherUserSuccess) {
      if (!otherUserData.team.name) {
        toast.warning("L'utente non ha una squadra");
        navigate('/classe');
      }
      setUser(otherUserData);
    }
  }, [other, otherUserSuccess, otherUserData]);

  useEffect(() => {
    if (isSuccess && classroom) {
      const reducedStudents = classroom.students.reduce((acc, student) => {
        acc[student.code] = student;
        return acc;
      }, {});
      setStudentsData(reducedStudents);

      const reducedTeachers = classroom.teachers.reduce((acc, teacher) => {
        acc[teacher.code] = teacher;
        return acc;
      }, {});
      setTeachersData(reducedTeachers);
    }
  }, [isSuccess, classroom]);

  if (isLoadingClassroom || (other && isLoadingOtherUser) || isLoadingRanking) {
    return <Loading />;
  }

  if (classroomError) {
    toast.warning(classroomError.message);
    return <Navigate to="/classe" />;
  }

  if (other && otherUserError) {
    toast.warning(otherUserError.message);
    return <Navigate to="/classe" />;
  }

  if (isErrorRanking) {
    toast.warning(rankingError.message);
    return <Navigate to="/classe" />;
  }

  return (
    <>
      <Row className="gx-3">
        <Col md={8} className="mb-3">
          <Card className="h-md-100">
            <Card.Body>
              <TeamHeader
                name={user.team.name || ''}
                description={user.team.description}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="h-md-100">
            <Card.Body className="d-flex align-items-center">
              <Container className="p-0">
                <Row>
                  <Col className="text-center border-end border-200 px-2">
                    <h4 className="mb-0">
                      {getPosition(user.username, ranking)}°
                    </h4>
                    <h6 className="text-600 fs-9 mt-1 mb-0">posizione</h6>
                  </Col>
                  <Col className="text-center border-end border-200 px-2">
                    <h4 className="mb-0">{user.score}</h4>
                    <h6 className="text-600 fs-9 mt-1 mb-0">punti</h6>
                  </Col>
                  <Col className="text-center px-2">
                    <h4 className="mb-0">{user.username}</h4>
                    <h6 className="text-600 fs-9 mt-1 mb-0">caposquadra</h6>
                  </Col>
                </Row>
              </Container>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="gx-3">
        <Col className="mb-3">
          <Card className="h-md-100">
            <Card.Body className="text-center">
              <div className="mb-4">
                <h5 className="mb-0 text-center text-700">
                  Professore capoclasse
                </h5>
                <Row className="text-center justify-content-center">
                  <Col className="mb-1">
                    <PlayerWidget
                      name={teachersData[user.team.teacher]?.name}
                      price={teachersData[user.team.teacher]?.price}
                      score={teachersData[user.team.teacher]?.score}
                      deleted={teachersData[user.team.teacher]?.deleted}
                      code={user.team.teacher}
                      type="score"
                      playerType="T"
                    />
                  </Col>
                </Row>
              </div>
              <div className="mb-4">
                <h5 className="mb-0 text-center text-700">Studenti</h5>
                <Row className="text-center">
                  {user.team.students.map(function (student, index) {
                    return (
                      <Col xs={6} lg={3} className="mb-1" key={index}>
                        <PlayerWidget
                          name={studentsData[student]?.name}
                          price={studentsData[student]?.price}
                          score={studentsData[student]?.score}
                          deleted={studentsData[student]?.deleted}
                          code={student}
                          type="score"
                          playerType="S"
                        />
                      </Col>
                    );
                  })}
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

Team.propTypes = {
  other: PropTypes.bool
};

export default Team;
